import { Injectable, OnDestroy, inject } from "@angular/core";
import { Subject, merge } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { AtgardMediatorService } from "../atgard-mediator.service";
import { KontraktMediatorService } from "../kontrakt-mediator.service";
import { TradslagMediatorService } from "../tradslag-mediator.service";
import { VirkeskostnaderUtbyteService } from "../virkeskostnader-utbyte.service";
import { VirkeskopValidationService } from "./virkeskop-validation.service";

@Injectable()
export class BeraknatUtbyteValidationService implements OnDestroy {
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);

    private readonly utbyteMetodSnabbkalkyl = "Snabbkalkyl";
    private readonly utbyteMetodDirektutbyte = "Direktutbyte";

    private readonly snabbKalkylChangedText = "Ett nytt utbyte har beräknats i Snabbkalkylen.";
    private readonly direktutbyteChangedText = "Ett nytt utbyte har beräknats i Direktutbytet.";

    private readonly unsubscribe$ = new Subject<void>();

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private get atgard() {
        return this.atgardMediatorService.atgard;
    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    initSubscriptions() {
        const tradslagChange$ = merge(
            this.tradslagMediatorService.loaded$,
            this.tradslagMediatorService.onSnabbkalkylCalculated$,
            this.tradslagMediatorService.onDirektutbyteCalculated$
        );
        tradslagChange$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.updateTradslag();
            });

        const atgardChange$ = merge(
            this.atgardMediatorService.atgard$,
            this.atgardMediatorService.atgardUpdate$
        );
        atgardChange$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.updateAreal();
                this.updateTradslag();
            });
    }

    private createValidationMessage(message: Partial<RuleValidationMessage>): RuleValidationMessage {
        return {
            severity: RuleSeverityLevel.warning,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "atgard",
            entityGuids: [
                this.kontrakt?.guid,
                this.atgard?.guid
            ],
            message: "",
            messageId: "",
            ...message
        };
    }

    private updateAreal() {
        const {
            nettoareal: atgardNettoareal, utbyteInlastAreal, utbyteInlastFran
        } = this.atgard;

        const arealDiffers = VirkeskostnaderUtbyteService.arealDiffers(atgardNettoareal, utbyteInlastAreal);
        const diffMessage = VirkeskostnaderUtbyteService.arealDiffMessage(utbyteInlastFran);

        this.validationService.updateClientSideValidation(arealDiffers, this.createValidationMessage({
            messageId: "beraknatUtbyteArealDiffers",
            message: diffMessage
        }));
    }

    private updateTradslag() {
        if (!this.tradslagMediatorService.loaded) {
            return;
        }

        const snabbkalkylTid = this.tradslagMediatorService.snabbkalkyl.beraknadTid;
        const direktutbyteTid = this.tradslagMediatorService.direktutbyte.beraknadTid;

        const hasChanged = (beraknadTid: Date, utbyteMetod: string) =>
            ObjectUtils.hasValue(beraknadTid)
            && ObjectUtils.hasValue(this.atgard?.utbyteInlastTid)
            && new Date(beraknadTid).getTime() > new Date(this.atgard?.utbyteInlastTid).getTime()
            && this.atgard?.utbyteInlastFran === utbyteMetod;

        const snabbkalkylChanged = hasChanged(snabbkalkylTid, this.utbyteMetodSnabbkalkyl);
        const direktutbyteChanged = hasChanged(direktutbyteTid, this.utbyteMetodDirektutbyte);

        this.validationService.updateClientSideValidation(snabbkalkylChanged, this.createValidationMessage({
            messageId: "beraknatUtbyteSnabbkalkylChanged",
            message: this.snabbKalkylChangedText
        }));

        this.validationService.updateClientSideValidation(direktutbyteChanged, this.createValidationMessage({
            messageId: "beraknatUtbyteDirektutbyteChanged",
            message: this.direktutbyteChangedText
        }));
    }
}
